import React, { useEffect, useState, useCallback } from "react";
import {
  Route, TrendingUp, TrendingDown, Activity, AlertTriangle, Clock, Award,
  DollarSign, Zap, Shield, Sparkles, RefreshCw, LayoutDashboard, Download,
} from "lucide-react";
import { ResponsiveContainer, XAxis, YAxis, Tooltip, Bar, BarChart, Cell } from "recharts";
import { dataApi } from "../api/client";

const fmtMoney = (v) =>
  new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN", maximumFractionDigits: 0 }).format(v || 0);
const fmtNum = (v) => new Intl.NumberFormat("es-PE").format(v || 0);

const RANGES = [
  { id: "today", label: "Hoy" },
  { id: "7d",    label: "7 días" },
  { id: "30d",   label: "30 días" },
  { id: "60d",   label: "60 días" },
  { id: "90d",   label: "90 días" },
];

function DeltaPill({ pct }) {
  const positive = (pct ?? 0) >= 0;
  const cls = positive ? "mdd-chip-success" : "mdd-chip-danger";
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span className={`mdd-chip ${cls}`}>
      <Icon className="w-3 h-3" />
      {positive ? "+" : ""}{pct}%
    </span>
  );
}

function KpiCard({ title, value, subtitle, delta, tone = "indigo", icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="mdd-card p-5 animate-fade-up">
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted">{title}</div>
        {Icon && (
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${
            tone === "neon"    ? "bg-mdd-lime/10 border-mdd-lime/30 text-mdd-lime"    :
            tone === "yellow"  ? "bg-mdd-yellow/10 border-mdd-yellow/30 text-mdd-yellow" :
            tone === "danger"  ? "bg-mdd-danger/10 border-mdd-danger/30 text-mdd-danger" :
            tone === "success" ? "bg-mdd-success/10 border-mdd-success/30 text-mdd-success" :
                                 "bg-mdd-neon/10 border-mdd-neon/30 text-mdd-neon"
          }`}>
            <Icon className="w-4 h-4" strokeWidth={1.75} />
          </div>
        )}
      </div>
      <div className={`mdd-kpi-number ${tone === "neon" ? "mdd-kpi-neon" : ""} text-3xl leading-none`}>
        {value}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-mdd-muted">{subtitle}</span>
        {delta !== undefined && delta !== null && <DeltaPill pct={delta} />}
      </div>
    </div>
  );
}

export default function DashboardKPIView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState("today");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await dataApi.analyticsDashboard(range);
      setData(r);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const handleExport = async () => {
    try {
      const blob = await dataApi.analyticsExport(range);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `panel_ejecutivo_${range}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const fin = data?.financial;
  const fleet = data?.fleet;
  const ops = data?.operations;
  const sch = data?.schedules;
  const health = data?.health;

  const rangeLabel = RANGES.find(r => r.id === range)?.label || range;

  return (
    <div data-testid="dashboard-kpi-view" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted flex items-center gap-2">
            <LayoutDashboard className="w-3 h-3" /> ANALYTICS
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-mdd-text mt-1">
            Panel Ejecutivo
          </h2>
          <p className="text-sm text-mdd-muted mt-1">
            Rango: <span className="text-mdd-lime font-mono">{rangeLabel}</span> · Auto-refresh 30s
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Selector de rango */}
          <div data-testid="range-selector" className="mdd-card !p-1 flex items-center gap-0.5">
            {RANGES.map((r) => (
              <button
                key={r.id}
                data-testid={`range-${r.id}`}
                onClick={() => setRange(r.id)}
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded-md transition-all ${
                  range === r.id
                    ? "bg-mdd-neon/20 text-mdd-lime border border-mdd-neon/30"
                    : "text-mdd-muted hover:text-mdd-text hover:bg-white/5"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            data-testid="dashboard-refresh"
            onClick={load}
            className="mdd-btn mdd-btn-ghost"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.75} />
            Refrescar
          </button>
          <button
            data-testid="dashboard-export"
            onClick={handleExport}
            className="mdd-btn mdd-btn-neon"
          >
            <Download className="w-4 h-4" strokeWidth={2} />
            Exportar CSV
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="dashboard-error" className="mdd-card p-4 border-mdd-danger/30 text-mdd-danger text-sm">
          Error: {error}
        </div>
      )}

      {loading && !data ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="mdd-card p-5 h-32 animate-pulse">
              <div className="w-16 h-3 bg-white/5 rounded mb-3" />
              <div className="w-24 h-8 bg-white/5 rounded mb-3" />
              <div className="w-full h-3 bg-white/5 rounded" />
            </div>
          ))}
        </div>
      ) : data ? (
        <>
          {/* Fila 1 — Financiero */}
          <section>
            <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted mb-2">Financiero</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <KpiCard
                testid="kpi-revenue-today"
                title="Ingresos hoy"
                value={fmtMoney(fin.revenue_today.value)}
                subtitle={`Ayer: ${fmtMoney(fin.revenue_today.prev)}`}
                delta={fin.revenue_today.delta_pct}
                tone="neon"
                icon={DollarSign}
              />
              <KpiCard
                testid="kpi-revenue-range"
                title={`Ingresos ${rangeLabel}`}
                value={fmtMoney(fin.revenue_range.value)}
                subtitle={`Período anterior: ${fmtMoney(fin.revenue_range.prev)}`}
                delta={fin.revenue_range.delta_pct}
                tone="indigo"
                icon={TrendingUp}
              />
              <KpiCard
                testid="kpi-commission-pending"
                title="Comisión pendiente"
                value={fmtMoney(fin.commission_pending)}
                subtitle="5% sobre carreras finalizadas"
                tone="yellow"
                icon={Sparkles}
              />
              <KpiCard
                testid="kpi-rides-today"
                title="Carreras hoy"
                value={fmtNum(ops.rides_today.created)}
                subtitle={`${ops.rides_today.completed} ok · ${ops.rides_today.cancelled} canceladas`}
                tone="indigo"
                icon={Route}
              />
            </div>
          </section>

          {/* Fila 2 — Flota + Operación + Salud */}
          <section>
            <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted mb-2">Flota · Operación · Salud</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard
                testid="kpi-drivers-online"
                title="Socios online"
                value={`${fleet.drivers_online}/${fleet.drivers_total}`}
                subtitle={`Ocupación ${fleet.occupancy_pct}%`}
                tone="success"
                icon={Zap}
              />
              <KpiCard
                testid="kpi-drivers-mora"
                title="Socios con mora"
                value={fmtNum(fleet.drivers_with_mora)}
                subtitle={`${fleet.drivers_inactive_30d} inactivos 30d`}
                tone={fleet.drivers_with_mora > 0 ? "danger" : "success"}
                icon={AlertTriangle}
              />
              <KpiCard
                testid="kpi-avg-assign"
                title="Tiempo asig."
                value={ops.avg_assign_seconds > 0 ? `${ops.avg_assign_seconds}s` : "—"}
                subtitle={`Rating pasajero: ${ops.avg_rating} ★`}
                tone="indigo"
                icon={Clock}
              />
              <KpiCard
                testid="kpi-health"
                title="Salud sistema"
                value={
                  health.sos_alerts_24h > 0 || health.bot_errors_24h > 5
                    ? "Atención"
                    : "OK"
                }
                subtitle={`SOS 24h: ${health.sos_alerts_24h} · Bot err: ${health.bot_errors_24h}`}
                tone={health.sos_alerts_24h > 0 ? "danger" : "success"}
                icon={Shield}
              />
            </div>
          </section>

          {/* Fila 3 — Timeline 24h + Ranking Top 5 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div data-testid="widget-timeline" className="mdd-card p-5 lg:col-span-2 animate-fade-up">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted">Actividad</div>
                  <div className="font-display font-semibold text-mdd-text text-lg mt-0.5">Carreras últimas 24h</div>
                </div>
                <span className="mdd-chip mdd-chip-neon">
                  <Sparkles className="w-3 h-3" />
                  Pico: {sch.hot_hours[0]?.hour ?? "--"}:00
                </span>
              </div>
              <div style={{ width: "100%", height: 200 }}>
                <ResponsiveContainer>
                  <BarChart data={ops.timeline_24h} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="barGradKPI" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor="#C6E90C" stopOpacity={0.95} />
                        <stop offset="100%" stopColor="#4D62F0" stopOpacity={0.55} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="hour"
                      tickFormatter={(h) => `${h}h`}
                      tick={{ fill: "#6B7280", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      axisLine={{ stroke: "#1E293B" }}
                      tickLine={false}
                      interval={2}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(77,98,240,0.08)" }}
                      contentStyle={{
                        backgroundColor: "rgba(10,19,38,0.95)",
                        border: "1px solid rgba(77,98,240,0.35)",
                        borderRadius: 10,
                        color: "#F3F4F6",
                        fontSize: 12,
                        fontFamily: "JetBrains Mono",
                      }}
                      labelFormatter={(l) => `Hora ${l}:00`}
                      formatter={(v) => [`${v} carreras`, ""]}
                    />
                    <Bar dataKey="rides" fill="url(#barGradKPI)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div data-testid="widget-top-drivers" className="mdd-card p-5 animate-fade-up">
              <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted">Ranking</div>
              <div className="font-display font-semibold text-mdd-text text-lg mt-0.5 mb-4 flex items-center gap-2">
                <Award className="w-4 h-4 text-mdd-yellow" strokeWidth={1.75} />
                Top socios {rangeLabel}
              </div>
              <div className="space-y-2">
                {fleet.top_drivers.length === 0 && (
                  <div className="text-xs text-mdd-muted py-4 text-center">Sin datos suficientes</div>
                )}
                {fleet.top_drivers.slice(0, 5).map((d, i) => (
                  <div key={d.driver_id} className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-white/5 transition-colors">
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold font-mono
                      ${i === 0 ? "bg-mdd-yellow/20 text-mdd-yellow border border-mdd-yellow/30" :
                        i === 1 ? "bg-mdd-neon/15 text-mdd-neon border border-mdd-neon/25" :
                                  "bg-white/5 text-mdd-muted border border-white/10"}`}>
                      #{i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-mdd-text truncate">{d.name}</div>
                      <div className="text-[10px] font-mono text-mdd-muted">{d.trips} viajes</div>
                    </div>
                    <div className="text-sm font-mono font-semibold text-mdd-lime">{fmtMoney(d.revenue)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* NUEVO: Gráfico Top 10 socios por ingresos */}
          {fleet.top_drivers.length > 0 && (
            <div data-testid="widget-top-drivers-chart" className="mdd-card p-5 animate-fade-up">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted">Comparativa</div>
                  <div className="font-display font-semibold text-mdd-text text-lg mt-0.5">
                    Top 10 socios por ingresos · {rangeLabel}
                  </div>
                </div>
                <span className="mdd-chip mdd-chip-indigo">
                  <Award className="w-3 h-3" />
                  {fleet.top_drivers.length} activos
                </span>
              </div>
              <div style={{ width: "100%", height: Math.max(220, fleet.top_drivers.length * 40) }}>
                <ResponsiveContainer>
                  <BarChart
                    data={fleet.top_drivers.map((d) => ({ name: d.name.split(" ")[0], revenue: d.revenue, trips: d.trips }))}
                    layout="vertical"
                    margin={{ top: 5, right: 40, left: 90, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient id="barTopGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%"   stopColor="#4D62F0" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#C6E90C" stopOpacity={0.95} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      type="number"
                      tick={{ fill: "#6B7280", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      axisLine={{ stroke: "#1E293B" }}
                      tickLine={false}
                      tickFormatter={(v) => `S/${v}`}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fill: "#9CA3AF", fontSize: 11, fontFamily: "Manrope" }}
                      axisLine={false}
                      tickLine={false}
                      width={80}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(77,98,240,0.06)" }}
                      contentStyle={{
                        backgroundColor: "rgba(10,19,38,0.95)",
                        border: "1px solid rgba(77,98,240,0.35)",
                        borderRadius: 10,
                        color: "#F3F4F6",
                        fontSize: 12,
                        fontFamily: "JetBrains Mono",
                      }}
                      formatter={(v, name, p) => {
                        if (name === "revenue") return [fmtMoney(v), "Ingresos"];
                        return [`${v} viajes`, "Trips"];
                      }}
                    />
                    <Bar dataKey="revenue" fill="url(#barTopGrad)" radius={[0, 8, 8, 0]}>
                      {fleet.top_drivers.map((_, i) => (
                        <Cell key={i} fill="url(#barTopGrad)" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Fila 4 — Payment + Hot hours + Dead hours */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div data-testid="widget-payment" className="mdd-card p-5 animate-fade-up">
              <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted">{rangeLabel}</div>
              <div className="font-display font-semibold text-mdd-text text-base mt-0.5 mb-3">Por método de pago</div>
              <div className="space-y-2">
                {fin.payment_breakdown.length === 0 && (
                  <div className="text-xs text-mdd-muted">Sin transacciones aún</div>
                )}
                {fin.payment_breakdown.map((p) => (
                  <div key={p.method} className="flex items-center justify-between text-sm">
                    <span className="capitalize text-mdd-muted">{p.method}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-mono text-mdd-muted">{p.count}x</span>
                      <span className="font-mono font-semibold text-mdd-text">{fmtMoney(p.total)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div data-testid="widget-hot-hours" className="mdd-card p-5 animate-fade-up">
              <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted">7d</div>
              <div className="font-display font-semibold text-mdd-text text-base mt-0.5 mb-3">Horas alta demanda</div>
              <div className="space-y-2">
                {sch.hot_hours.length === 0 && (
                  <div className="text-xs text-mdd-muted">Sin patrón detectado</div>
                )}
                {sch.hot_hours.map((h, i) => (
                  <div key={h.hour} className="flex items-center gap-3">
                    <div className="font-mono text-lg font-bold text-mdd-lime w-14">{h.hour}:00</div>
                    <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-mdd-neon to-mdd-lime"
                        style={{ width: `${100 - i * 25}%` }}
                      />
                    </div>
                    <div className="text-xs font-mono text-mdd-muted w-14 text-right">{h.count} viajes</div>
                  </div>
                ))}
              </div>
            </div>

            <div data-testid="widget-dead-hours" className="mdd-card p-5 animate-fade-up">
              <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted">7d</div>
              <div className="font-display font-semibold text-mdd-text text-base mt-0.5 mb-3">Horas muertas</div>
              <div className="flex flex-wrap gap-1.5">
                {sch.dead_hours.length === 0 && (
                  <div className="text-xs text-mdd-muted">Sin franjas muertas</div>
                )}
                {sch.dead_hours.map((h) => (
                  <span key={h} className="mdd-chip mdd-chip-indigo !text-[10px]">
                    {String(h).padStart(2, "0")}:00
                  </span>
                ))}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
